/**
 * @param calendarDate - The date in the format yyyy-mm-dd
 * @returns A Date object representing the calendar date
 */
const getDateFromCalendarDate = (calendarDate: string): Date => {
  const [year, month, day] = calendarDate.split("-").map(Number);
  return new Date(year, month - 1, day); // months are 0-indexed in JavaScript
};

/**
 * @returns The current date in the format yyyy-mm-dd
 */
const getTodayAsCalendarDate = () => {
  const today = new Date();
  return today.toISOString().split("T")[0]; // yyyy-mm-dd
};

/**
 * @param date - The date of birth
 * @param atDate - The date to calculate the age on (defaults to current date).
 * @returns The age of the person in years.
 */
const getAgeFromDate = (date: Date, atDate: Date = new Date()): number => {
  let age = atDate.getFullYear() - date.getFullYear();

  // Adjust age if birthday hasn't occurred yet this year
  const monthDiff = atDate.getMonth() - date.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && atDate.getDate() < date.getDate())) {
    age--;
  }

  return age;
};

/**
 * @description Calculates the age of a person based on their date of birth and
 * the date to calculate the age on.
 *
 * Note: By default, this util determines an age in the environment's local
 * timezone at the time of the request. In other words, it does not calculate
 * the age of the person at the time of travel in the time zone of the travel
 * origin. If the travel date is impotant you should pass in the date of travel
 * as the second argument. If time zone is important, you may want to use a
 * custom util.
 *
 * @param calendarDateOfBirth - The date of birth in the format yyyy-mm-dd
 * @param atCalendarDate - The date to calculate the age on (defaults to current date).
 * @returns The age of the person in years.
 */
export const getAge = (
  calendarDateOfBirth: string,
  atCalendarDate = getTodayAsCalendarDate()
) => {
  // Parse the date string to avoid timezone conversion issues
  const normalizedDob = getDateFromCalendarDate(calendarDateOfBirth);
  const normalizedAtDate = getDateFromCalendarDate(atCalendarDate);
  return getAgeFromDate(normalizedDob, normalizedAtDate);
};
